import { Component, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ColumnMode, SelectionType } from '@swimlane/ngx-datatable';
import { ManualUploadDownloadService } from './manual-upload-download.service';
import { ManualUploadProcStatusService } from '../manual-upload-proc-status/manual-upload-proc-status.service';
import { GlobalService } from '../global.service';
import * as XLSX from 'xlsx';

export interface UploadType {
  id: string;
  label: string;
}

export interface S3File {
  fileName: string;
  lastModified: string;
  size: string;
}

@Component({
    selector: 'app-manual-upload-download',
    templateUrl: './manual-upload-download.component.html',
    styleUrls: ['./manual-upload-download.component.css'],
    standalone: false
})
export class ManualUploadDownloadComponent implements OnInit {

  // ─── Messages ────────────────────────────────────────────────────────────────
  errorMessage: string = '';
  successMessage: string = '';
  spinnerMessage: string = 'Please Wait...';

  // ─── Role / NBPC ─────────────────────────────────────────────────────────────
  roleAccess: any = [];
  Roledata: any = [];
  accessEnable: boolean = true;

  // ─── Table ───────────────────────────────────────────────────────────────────
  ColumnMode = ColumnMode;
  SelectionType = SelectionType;
  limitSelected: number = 50;
  step: number = 0;

  setStep(index: number) { this.step = index; }

  // ─── Upload ──────────────────────────────────────────────────────────────────
  // IDs match the base S3 folder names in fileexportpoc (Lambda appends dev/qa/prod suffix)
  uploadTypes: UploadType[] = [
    { id: 'Partmstr',        label: 'Part Master'       },
    { id: 'PcVehSpecIndex',  label: 'eBoM SVI Upload' },
    { id: 'PcSpecUpload',    label: 'PC Spec Upload'    },
    { id: 'Prodpartcontent', label: 'Prod Part Content' },
    { id: 'Prodpartstruc',   label: 'Prod Part Struc'   },
    { id: 'ZpfUpload',       label: 'ZPF Upload'        },
  ];

  // Maps upload type IDs to proc_nm values stored in the DB
  private readonly UPLOAD_TYPE_TO_PROC_NM: Record<string, string> = {
    'Partmstr':        'PART_MASTER_UPLOAD',
    'PcVehSpecIndex':  'PC_VEH_SPEC_INDEX_UPLOAD',
    'PcSpecUpload':    'PC_SPEC_UPLOAD',
    'Prodpartcontent': 'PROD_PART_CONTENT_UPLOAD',
    'Prodpartstruc':   'PROD_PART_STRUC_UPLOAD',
    'ZpfUpload':       'ZPF_UPLOAD',
  };

  private readonly TYPES_WITH_TEMPLATE = ['Partmstr', 'PcVehSpecIndex', 'PcSpecUpload', 'Prodpartcontent', 'Prodpartstruc', 'ZpfUpload'];

  // localStorage key holding the SHA-256 hash of the last uploaded eBoM SVI file
  private readonly SVI_LAST_HASH_KEY = 'ebomSviLastUploadHash';

  // Current status per proc_nm, populated on load
  uploadStatusMap: Record<string, string> = {};

  // Required column headers for each upload type, derived from sample files
  private readonly EXPECTED_COLUMNS: Record<string, string[]> = {
    'Partmstr': [
      'PART_NO', 'FAMILY', 'UOM_NM', 'PART_DESC',
      'EFF_DT', 'EXPR_DT', 'CREATE_BY_ID', 'UPDATE_BY_ID'
    ],
    'PcVehSpecIndex': [
      'Model', 'Called Model Code', 'CTL Model Code', 'Spec Item(5)', 'Detail Code(5)', 'Digit', 'Code',
      'Digit_1', 'Code_2', 'Digit_3', 'Code_4', 'S or P', 'Set Cond.', 'Limited ID',
      'Original Tech Notice No.', 'Tech Notice No. From', 'Tech Notice No. To', 'T/C Revised Sequence No.',
      'Notice No. From', 'Sub-Notice No. From', 'Notice No. To', 'Sub-Notice No. To',
      'Clear FLG', 'Clear Notice No.', 'Clear Sub-Notice No.', 'T/C From', 'T/C To', 'MT Date/Time',
      'Kat Spec-Tech Notice', 'PC Spec', 'Kat Spec- 3', 'Time in 1', 'Time out 1', 'Time in 2',
      'Time out 2', 'Status 2'
    ],
    'PcSpecUpload': [
      'Model', 'Spec Item(5)', 'Detail Code(5)', 'Digit', 'Code',
      'Spec Name Kanji', 'Spec Name Kana', 'Spec Name English',
      'Name MTFLG', 'Tech Notice No. From', 'Notice No. From',
      'Sub-Notice No. From', 'MT Date/Time'
    ],
    'Prodpartcontent': [
      '#CHNG_TYP_CD', 'SRC_XREF_SEQ_NBR', 'USER_CODE', 'FAMILY',
      'KATASHIKI_CD', 'GRPI_CD', 'HIKIATE_STAT_CD', 'MFG_SPEC_CD',
      'PC_SPEC_CD', 'EFF_DT', 'EXPR_DT', 'BASE_OPTN_TYP_CD',
      'PLNT_CMT_TXT', 'COMMENTS', 'LOADED_BY_ID'
    ],
    'Prodpartstruc': [
      '#CHNG_TYP_CD', 'SRC_XREF_SEQ_NBR', 'USER_CODE', 'FAMILY',
      'GRPI_CD', 'PARENT_PART', 'CHILD_PART', 'SLCTV_CD',
      'PER_VEH_QTY', 'EFF_DT', 'EXPR_DT', 'HS_CODE',
      'COMPLETE_ROUTING', 'COMMENTS', 'LOADED_BY_ID'
    ],
    'ZpfUpload': [
      '#NAMC', 'SUPPLIER', 'BUYER', 'PART_NO',
      'EFF_DT', 'EXPR_DT', 'ACT_UNIT_COST', 'TRN_UNIT_COST', 'STD_UNIT_COST'
    ]
  };
  // TODO(backend): S9 Lambda must INSERT into MANUAL_UPLOAD_PROCESSING table and send SES email after S3 write.
  selectedUploadType: string | null = null;
  selectedFile: File | null = null;
  selectedFileName: string = '';

  // ─── Download ─────────────────────────────────────────────────────────────────
  downloadTypes: UploadType[] = [
    { id: 'BOM_STRUCTURE',  label: 'Generate BOM Structure' },
    { id: 'MONTHLY_REPORT', label: 'Monthly Report'         },
  ];
  selectedDownloadType: string | null = null;
  s3Files: S3File[] = [];
  selectedS3File: string = '';
  selectedS3FileRows: any[] = [];
  loadingS3Files: boolean = false;

  constructor(
    private spinnerService: NgxSpinnerService,
    private service: ManualUploadDownloadService,
    private procStatusService: ManualUploadProcStatusService,
    public globalrole: GlobalService
  ) {}

  ngOnInit(): void {
    (async () => {
      this.spinnerService.show();
      await this.userroleAccessfn();
      if (this.accessEnable) { this.loadUploadStatuses(); }
      this.spinnerService.hide();
    })();
  }

  // Fetches current status for all 5 upload types and populates uploadStatusMap
  private loadUploadStatuses(): void {
    this.procStatusService.getUploadProcStatusList(null, null).subscribe({
      next: (res: any) => {
        (res.payload || []).forEach((row: any) => {
          this.uploadStatusMap[row.upload_type] = row.upload_status;
        });
      }
    });
  }

  // Returns the current status for the selected upload type
  getSelectedUploadStatus(): string | null {
    if (!this.selectedUploadType) { return null; }
    const procNm = this.UPLOAD_TYPE_TO_PROC_NM[this.selectedUploadType];
    return this.uploadStatusMap[procNm] || null;
  }

  // Upload is blocked unless status is COMPLETED or never started
  canUploadSelectedType(): boolean {
    const status = this.getSelectedUploadStatus();
    return !status || status === 'COMPLETED' || status === 'ERROR';
  }

  hasTemplate(type: string | null): boolean {
    return !!type && this.TYPES_WITH_TEMPLATE.includes(type);
  }

  statusClass(status: string): string {
    const map: Record<string, string> = {
      'COMPLETED':   'status-completed',
      'STARTED':     'status-started',
      'INITIATED':   'status-initiated',
      'IN_PROGRESS': 'status-inprogress',
      'ERROR':       'status-error',
    };
    return map[status] || '';
  }

  // ─── Upload Handlers ─────────────────────────────────────────────────────────

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) { return; }

    const file = input.files[0];
    // Reset input so re-selecting same file triggers change event again
    input.value = '';

    // 1. Screen must be selected first
    if (!this.selectedUploadType) {
      this.errorMessage = 'Please select a Screen Name before choosing a file.';
      return;
    }

    // 2. Validate file extension
    const allowedExts = ['.xlsx', '.xls', '.csv'];
    const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!allowedExts.includes(fileExt)) {
      this.errorMessage = `Invalid file type. Supported formats: .xlsx, .xls, .csv`;
      this.selectedFile = null;
      this.selectedFileName = '';
      return;
    }

    // 3. Validate file size (max 10 MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      this.errorMessage = `File size exceeds 10 MB limit. Please upload a smaller file.`;
      this.selectedFile = null;
      this.selectedFileName = '';
      return;
    }

    // 4. Async: validate column headers against expected columns for this screen
    this.validateFileColumns(file);
  }

  private validateFileColumns(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const firstSheet = wb.Sheets[wb.SheetNames[0]];
        const rows: any[][] = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

        if (!rows || rows.length === 0) {
          this.errorMessage = 'File appears to be empty.';
          this.selectedFile = null;
          this.selectedFileName = '';
          return;
        }

        const fileHeaders = (rows[0] as any[]).map((h: any) => String(h ?? '').trim());
        const expected = this.EXPECTED_COLUMNS[this.selectedUploadType!];

        if (expected) {
          const missing = expected.filter(col => !fileHeaders.includes(col));
          const extra   = fileHeaders.filter(col => !expected.includes(col));

          if (missing.length > 0 || extra.length > 0) {
            const parts: string[] = [];
            if (missing.length) { parts.push(`Missing: ${missing.join(', ')}`); }
            if (extra.length)   { parts.push(`Unexpected: ${extra.join(', ')}`); }
            this.errorMessage = `Wrong format — column mismatch. ${parts.join(' | ')}`;
            this.selectedFile = null;
            this.selectedFileName = '';
            return;
          }
        }

        // All validations passed
        this.closeAlert();
        this.selectedFile = file;
        this.selectedFileName = file.name;
      } catch {
        this.errorMessage = 'Could not read file. Ensure it is a valid Excel or CSV file.';
        this.selectedFile = null;
        this.selectedFileName = '';
      }
    };
    reader.readAsArrayBuffer(file);
  }

  onUpload(): void {
    this.closeAlert();
    if (!this.roleAccess.includes('Update')) {
      this.errorMessage = "User does not have permission to perform 'update' operation";
      return;
    }
    if (!this.selectedUploadType) {
      this.errorMessage = 'Please select an upload type.';
      return;
    }
    if (!this.canUploadSelectedType()) {
      this.errorMessage = `Upload for this type is currently ${this.getSelectedUploadStatus()}. Please wait until it is COMPLETED before uploading again.`;
      return;
    }
    if (!this.selectedFile) {
      this.errorMessage = 'Please select a file to upload.';
      return;
    }
    const uploadType = this.uploadTypes.find(t => t.id === this.selectedUploadType);
    if (!uploadType) { return; }

    // eBoM SVI upload: block re-uploading the same file content
    if (this.selectedUploadType === 'PcVehSpecIndex') {
      this.spinnerService.show();
      this.computeFileHash(this.selectedFile).then(hash => {
        const lastHash = localStorage.getItem(this.SVI_LAST_HASH_KEY);
        if (lastHash && lastHash === hash) {
          this.spinnerService.hide();
          this.errorMessage = 'This file has already been uploaded. Please upload a file with different content.';
          return;
        }
        this.doUpload(hash);
      }).catch(() => {
        this.spinnerService.hide();
        this.errorMessage = 'Could not read file. Please try again.';
      });
      return;
    }

    this.spinnerService.show();
    this.doUpload(null);
  }

  private doUpload(sviHash: string | null): void {
    this.service.uploadFile(this.selectedUploadType!, this.selectedFile!, sviHash || undefined).subscribe({
      next: (res: any) => {
        this.spinnerService.hide();
        this.successMessage = 'Upload initiated. You will receive an email shortly.';
        // Persist hash so identical eBoM SVI content is rejected next time
        if (sviHash) { localStorage.setItem(this.SVI_LAST_HASH_KEY, sviHash); }
        // Optimistically mark status as STARTED in the local map
        const procNm = this.UPLOAD_TYPE_TO_PROC_NM[this.selectedUploadType!];
        if (procNm) { this.uploadStatusMap[procNm] = 'STARTED'; }
        this.selectedUploadType = null;
        this.selectedFile = null;
        this.selectedFileName = '';
      },
      error: (err: any) => {
        this.spinnerService.hide();
        // Backend returns 409 when the same eBoM SVI content was already uploaded
        if (err?.status === 409) {
          this.errorMessage = err?.error?.message ||
            'This file has already been uploaded. Please upload a file with different content.';
          return;
        }
        this.errorMessage = err?.error?.message || 'Upload failed. Please try again.';
      }
    });
  }

  // SHA-256 hash of file content, used to detect duplicate eBoM SVI uploads
  private computeFileHash(file: File): Promise<string> {
    return file.arrayBuffer()
      .then(buffer => crypto.subtle.digest('SHA-256', buffer))
      .then(digest => Array.from(new Uint8Array(digest))
        .map(b => b.toString(16).padStart(2, '0'))
        .join(''));
  }

  onS3FileSelect({ selected }: any): void {
    this.selectedS3FileRows = selected;
    this.selectedS3File = selected && selected.length > 0 ? selected[0].fileName : '';
  }

  // ─── Download Handlers ────────────────────────────────────────────────────────

  onDownloadTypeChange(): void {
    this.s3Files = [];
    this.selectedS3File = '';
    this.selectedS3FileRows = [];
    this.closeAlert();
    if (!this.roleAccess.includes('Read')) {
      this.errorMessage = "User does not have permission to perform 'read' operation";
      return;
    }
    if (!this.selectedDownloadType) { return; }
    this.loadingS3Files = true;
    this.spinnerService.show();
    this.service.getS3FileList(this.selectedDownloadType).subscribe({
      next: (res: any) => {
        this.spinnerService.hide();
        this.loadingS3Files = false;
        const files = res?.payload || [];
        if (files.length === 0) {
          this.errorMessage = 'No files found for the selected report type.';
          return;
        }
        this.s3Files = files;
      },
      error: (err: any) => {
        this.spinnerService.hide();
        this.loadingS3Files = false;
        this.errorMessage = err?.error?.message || 'Failed to load files. Please try again.';
      }
    });
  }

  onDownload(): void {
    this.closeAlert();
    if (!this.roleAccess.includes('Read')) {
      this.errorMessage = "User does not have permission to perform 'read' operation";
      return;
    }
    if (!this.selectedDownloadType) {
      this.errorMessage = 'Please select a download type.';
      return;
    }
    if (!this.selectedS3File) {
      this.errorMessage = 'Please select a file from the grid to download.';
      return;
    }
    this.spinnerService.show();
    this.service.downloadFile(this.selectedDownloadType, this.selectedS3File).subscribe({
      next: (res: any) => {
        this.spinnerService.hide();
        const downloadUrl = res?.payload?.[0]?.downloadUrl;
        if (!downloadUrl) {
          this.errorMessage = 'Failed to get download URL. Please try again.';
          return;
        }
        // Open presigned URL → browser triggers download
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = this.selectedS3File;
        a.click();
        this.successMessage = `"${this.selectedS3File}" downloaded successfully.`;
      },
      error: (err: any) => {
        this.spinnerService.hide();
        this.errorMessage = err?.error?.message || 'Download failed. Please try again.';
      }
    });
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  onDownloadTemplate(): void {
    this.closeAlert();
    if (!this.selectedUploadType) {
      this.errorMessage = 'Please select a Screen Name to download its template.';
      return;
    }
    this.spinnerService.show();
    this.service.getUploadTemplate(this.selectedUploadType).subscribe({
      next: (res: any) => {
        this.spinnerService.hide();
        const downloadUrl = res?.payload?.[0]?.downloadUrl;
        if (!downloadUrl) {
          this.errorMessage = 'Failed to get template URL. Please try again.';
          return;
        }
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${this.selectedUploadType}_template.xlsx`;
        a.click();
      },
      error: (err: any) => {
        this.spinnerService.hide();
        this.errorMessage = err?.error?.message || 'Failed to download template. Please try again.';
      }
    });
  }

  resetUpload(): void {
    this.selectedUploadType = null;
    this.selectedFile = null;
    this.selectedFileName = '';
    this.closeAlert();
  }

  resetDownload(): void {
    this.selectedDownloadType = null;
    this.s3Files = [];
    this.selectedS3File = '';
    this.selectedS3FileRows = [];
    this.closeAlert();
  }

  // ─── Role Access ─────────────────────────────────────────────────────────────

  userroleAccessfn() {
    return new Promise(resolve => {
      this.globalrole.Roledetails.subscribe(res => {
        this.Roledata = res;
      })
      if (this.Roledata.length != 0) {
        this.Roledata.filter((res: any) => {
          if (res.function_nm == 'NBPC_10116') {
            this.roleAccess.push(res.access_desc);
          }
        })
        if (this.roleAccess.length > 0) {
          this.accessEnable = true;
          resolve(true)
        } else {
          this.roleAccess = [];
          this.errorMessage = "User does not have permission to perform 'read' operation";
          this.accessEnable = false;
          this.spinnerService.hide();
          resolve(false)
        }
      } else if (this.Roledata.length == 0) {
        let arrayvalue: any; let roleList: any;
        let data = localStorage.getItem('role');
        arrayvalue = (data || '').split(',')
        let str = '';
        for (let i = 0; i < arrayvalue.length; i++) {
          if (str == '') {
            str = "'" + arrayvalue[i] + "'"
          } else {
            str += ',' + "'" + arrayvalue[i] + "'";
          }
          roleList = str;
        }
        let body = {
          security_grp_nm: roleList
        }
        let userpreviliagedata = localStorage.getItem('userpreviliagedata')
        if (userpreviliagedata && userpreviliagedata.length != 0 && userpreviliagedata != '') {
          this.globalrole.changeMessage(JSON.parse(userpreviliagedata))
          this.globalrole.Roledetails.subscribe(res => { this.Roledata = res; })
          this.Roledata.filter((res: any) => {
            if (res.function_nm == 'NBPC_10116') {
              this.roleAccess.push(res.access_desc);
            }
          })
          if (this.roleAccess.length > 0) {
            this.accessEnable = true;
            resolve(true)
          } else {
            this.roleAccess = [];
            this.errorMessage = "User does not have permission to perform 'read' operation";
            this.accessEnable = false;
            this.spinnerService.hide();
            resolve(false)
          }
        } else {
          this.roleAccess = [];
          this.errorMessage = "User does not have permission to perform 'read' operation";
          this.accessEnable = false;
          this.spinnerService.hide();
          resolve(false)
        }
      }
    })
  }

  closeAlert(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }
}
